import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ROUTES } from 'theSource/app/routes';
import { LOGOFF_QUERY_KEY } from '../Header/components/utils';
import { useApiMutation } from 'theSource/utils';
import {
  resetTermsOfUseAcceptance,
  setAuthenticated,
} from 'theSource/features';
import { setIsAutomaticAuthDisabled } from 'theSource/services';
import {
  useCompanySettingsQuery,
  useConfigurationQuery,
} from 'theSource/queries';
import { PsButtonTuner } from 'stencilSetup';
import {
  setHelpSearchString,
  setLogoutClicked,
} from '../../../features/HomePage/services';
import { useToastDialog } from 'enrollment/components/Shared/ToastDialogContext';

export type LogoutButtonProps = {
  dataTestId?: string;
};

export const LogoutButton = ({ dataTestId }: LogoutButtonProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('pages.more');
  const { configuration } = useConfigurationQuery();
  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(configuration?.apiHost),
  });

  const toast = useToastDialog();

  const { isLoading, mutate: logoff } = useApiMutation<{}, {}, {}>({
    queryKey: LOGOFF_QUERY_KEY,
    clearQueryCache: true,
    method: 'put',
    endpoint: '/logoff',
    onSuccess: () => {
      dispatch(setAuthenticated(false));
      dispatch(resetTermsOfUseAcceptance());
      dispatch(setIsAutomaticAuthDisabled(true));
      dispatch(setLogoutClicked(true));
      dispatch(setHelpSearchString(''));
      const chatBotDrawerOpen = document.querySelector('.chat-bot');
      if (chatBotDrawerOpen) {
        chatBotDrawerOpen.remove();
      }
      sessionStorage.removeItem("olive-chatbot-conversation"); // clear employee chatbot conversation history
      handleNavigation();
    },
    showSuccessSnackbar: false,
    toastRef: toast
  });

  const handleNavigation = () => {
    const sourceEnabled = companySettings?.data.source_license;
    const apiHost = configuration?.apiHost;
    if (!sourceEnabled && apiHost) {
      return (window.location.href = apiHost);
    }
    return navigate(ROUTES.main.home.path);
  };

  return (
    <PsButtonTuner
      data-testid={dataTestId || null}
      buttonId="logoutButton"
      label={t('logOut')}
      level="ternary"
      iconRight="sign-out"
      extraClass="btn-block"
      isLoading={isLoading}
      onClick={() => logoff({})}
    />
  );
};
